import { PaletteMode } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { GoogleOAuthProvider } from "@react-oauth/google";
import React, { useEffect, useState } from "react";
import { Route, BrowserRouter as Router, Routes, useLocation } from "react-router-dom";
import { trackPageView } from "./src/utils/gtagUtils";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import LoadingScreen from "./src/components/LoadingScreen";
import { useWhitelabel } from "./src/context/WhitelabelContext";
import { useAuth } from "./src/context/AuthContext";
import ShareableAgentPage from "./src/agents/ShareableAgentPage";
import PublicList from "./src/dashboard/lists/PublicList";
import SharedConversation from "./src/conversation/SharedConversation";
import Dashboard from "./src/dashboard/Dashboard";
import HomePage from "./src/home/HomePage";
import Login from "./src/login/Login";
import Pricing from "./src/pricing/Pricing";
import Roadmap from "./src/roadmap/Roadmap";
import Security from "./src/security/Security";
import SecurityHallOfFame from "./src/security/SecurityHallOfFame";
import VulnerabilityDisclosurePolicy from "./src/security/VulnerabilityDisclosurePolicy";
import Status from "./src/status/Status";
import Videos from "./src/videos/Videos";
import PrivacyPolicy from "./src/legal/PrivacyPolicy";
import TermsOfService from "./src/legal/TermsOfService";
import LoginSso from "./src/login/LoginSso";
import MagicLinkVerify from "./src/login/MagicLinkVerify";
import GoogleCallback from "./src/login/GoogleCallback";
import Onboarding from "./src/onboarding/Onboarding";
import Subscribe from "./src/subscribe/Subscribe";
import PublicQuotePayment from "./src/PublicQuotePayment";
import ExternalAppHost from "./src/external-app/ExternalAppHost";
import { getInstanceConfig, stripePublicId } from "./src/utils";

// Initialize Stripe
const stripePromise = loadStripe(stripePublicId);

// Helper to get the font-family CSS value for any Google Font
const getFontFamily = (fontName?: string, fallback?: string): string => {
  if (!fontName) return fallback || "Inter, Arial, sans-serif";
  // Wrap font name in quotes if it contains spaces
  const formattedName = fontName.includes(" ") ? `'${fontName}'` : fontName;
  return `${formattedName}, sans-serif`;
};

const getTheme = (mode: PaletteMode, whitelabelSettings?: any) => {
  const instanceConfig = getInstanceConfig(mode, whitelabelSettings);

  // Get custom fonts from whitelabel settings, with defaults
  const headingFont = getFontFamily(
    whitelabelSettings?.headingFontFamily,
    "Cardo, serif"
  );
  const bodyFont = getFontFamily(
    whitelabelSettings?.bodyFontFamily,
    "Inter, Arial, sans-serif"
  );

  var theme = {
    palette: {
      mode: mode,
      primary: {
        main: instanceConfig.primaryColor,
        contrastText: instanceConfig.contrastColor,
      },
      secondary: {
        main: instanceConfig.secondaryColor,
      },
      success: {
        main: mode === "dark" ? "#A3F7BF" : "#29A19C",
      },
    },
    background: {
      default: instanceConfig.backgroundColor,
      paper: mode === "dark" ? "#0d0d0d" : "#ffffff",
    },
    typography: {
      fontFamily: bodyFont,
      h1: { fontFamily: headingFont },
      h2: { fontFamily: headingFont },
      h3: { fontFamily: headingFont },
      h4: { fontFamily: headingFont },
      h5: { fontFamily: headingFont },
      h6: { fontFamily: headingFont },
      body1: { fontFamily: bodyFont },
      body2: { fontFamily: bodyFont },
      caption: { fontFamily: bodyFont },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            // iOS standalone/PWA mode safe area handling - applied globally
            paddingTop: "env(safe-area-inset-top, 0px)",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
          },
          // MDXEditor content area - prevent text overflow and add padding
          ".editor-content-editable": {
            wordWrap: "break-word",
            overflowWrap: "break-word",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxWidth: "100%",
            padding: "16px",
            minHeight: "100%",
            boxSizing: "border-box",
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              color: instanceConfig.primaryColor,
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            "&.Mui-checked": {
              color: instanceConfig.primaryColor,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          contained: {
            color: instanceConfig.containedTextColor,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          filledPrimary: {
            color: instanceConfig.containedTextColor,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          root: {
            "& .MuiBackdrop-root": {
              backgroundColor:
                mode === "dark"
                  ? "rgba(0, 0, 0, 0.85)"
                  : "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
            },
          },
          paper: {
            backgroundImage: "none",
            backgroundColor: mode === "dark" ? "#0d0d0d" : "#ffffff",
            overscrollBehavior: "contain",
          },
          paperScrollBody: {
            overscrollBehavior: "contain",
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            overscrollBehavior: "contain",
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            // Safe area spacing for iOS standalone/PWA mode
            marginTop: "env(safe-area-inset-top, 0px)",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: mode === "dark" ? "#0d0d0d" : "#ffffff",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: "none",
            backgroundColor: mode === "dark" ? "#0d0d0d" : "#ffffff",
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          text: {
            // Make text black for primary domain in dark mode
            fill:
              instanceConfig.isMainSite && mode === "dark"
                ? "#000000"
                : undefined,
          },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              color: instanceConfig.containedTextColor,
            },
          },
        },
      },
    },
  };
  // @ts-ignore
  theme.palette.background = {
    default: instanceConfig.backgroundColor,
  };
  return createTheme(theme);
};

// Component to track page views in Google Analytics for SPA navigation
const PageTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
};

// Component to render the appropriate root page based on whitelabel settings
const RootPageRouter: React.FC<{
  mode: string;
  whitelabelSettings: any;
}> = ({ mode, whitelabelSettings }) => {
  // Determine which page to show based on rootPage setting
  // Default to 'login' for backward compatibility (both main site and whitelabels)
  const rootPage = whitelabelSettings?.rootPage || "login";

  // Show home page only if explicitly configured
  if (rootPage === "home") {
    return <HomePage mode={mode} />;
  }

  // Default: show login page (backward compatible)
  return <Login mode={mode} isLoginParam={true} />;
};

export default function AppContent() {
  const [mode, setMode] = useState("system");
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const { whitelabelSettings, loading } = useWhitelabel();

  var theme = null;
  var instanceConfig = null;
  if (mode === "system") {
    theme = getTheme(prefersDarkMode ? "dark" : "light", whitelabelSettings);
    setMode(prefersDarkMode ? "dark" : "light");
    instanceConfig = getInstanceConfig(
      prefersDarkMode ? "dark" : "light",
      whitelabelSettings
    );
  } else {
    theme = getTheme(mode as PaletteMode, whitelabelSettings);
    instanceConfig = getInstanceConfig(mode, whitelabelSettings);
  }

  const restoreToken = () => {
    const mode = window.localStorage.getItem("mode");
    if (mode) {
      setMode(mode);
    }
  };

  const setThemeMode = (mode: string) => {
    window.localStorage.setItem("mode", mode);
    setMode(mode);
  };

  const setThemeForOnePage = (mode: string) => {
    setMode(mode);
  };

  useEffect(() => {
    restoreToken();
  }, []);

  // Update document title when whitelabel settings change
  useEffect(() => {
    if (whitelabelSettings?.headTitle) {
      document.title = whitelabelSettings.headTitle;
    }
  }, [whitelabelSettings]);

  // Load Google Fonts when whitelabel settings change
  useEffect(() => {
    if (!whitelabelSettings) return;

    // Build list of fonts to load
    const fontsToLoad: string[] = [];

    // Add heading font if set
    if (whitelabelSettings.headingFontFamily) {
      fontsToLoad.push(whitelabelSettings.headingFontFamily);
    }

    // Add body font if set
    if (whitelabelSettings.bodyFontFamily) {
      fontsToLoad.push(whitelabelSettings.bodyFontFamily);
    }

    // Load fonts via Google Fonts CSS API
    if (fontsToLoad.length > 0) {
      const uniqueFonts = [...new Set(fontsToLoad)];
      const fontQuery = uniqueFonts
        .map((font) => font.replace(/ /g, "+") + ":wght@400;500;600;700")
        .join("&family=");
      const fontUrl = `https://fonts.googleapis.com/css2?family=${fontQuery}&display=swap`;

      // Check if this font link already exists
      const existingLinks = document.querySelectorAll('link[rel="stylesheet"]');
      let linkExists = false;
      existingLinks.forEach((link) => {
        if ((link as HTMLLinkElement).href === fontUrl) {
          linkExists = true;
        }
      });
      if (!linkExists) {
        const link = document.createElement("link");
        link.href = fontUrl;
        link.rel = "stylesheet";
        document.head.appendChild(link);
      }
    }
  }, [whitelabelSettings]);

  if (loading) {
    return <LoadingScreen open={true} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <GoogleOAuthProvider clientId="235414097748-5fpehtnvd7nuhud1vm5atupldi9945tv.apps.googleusercontent.com">
        <CssBaseline />
        <Router>
          <PageTracker />
          <Routes>
            <Route
              path="/"
              element={
                <RootPageRouter
                  mode={mode}
                  whitelabelSettings={whitelabelSettings}
                />
              }
            />
            <Route path="/home" element={<HomePage mode={mode} />} />
            <Route
              path="/conversation/:conversationId"
              element={<SharedConversation mode={mode} />}
            />
            <Route
              path="/a/:shortCode"
              element={<ShareableAgentPage mode={mode} />}
            />
            <Route path="/x/:appId" element={<ExternalAppHost />} />
            <Route
              path="/public/lists/:listId"
              element={<PublicList mode={mode} />}
            />
            <Route
              path="/login"
              element={<Login mode={mode} isLoginParam={true} />}
            />
            <Route
              path="/signup"
              element={<Login mode={mode} isLoginParam={false} />}
            />
            <Route
              path="/login/sso"
              element={<LoginSso mode={mode} />}
            />
            <Route
              path="/auth/verify"
              element={<MagicLinkVerify />}
            />
            <Route
              path="/auth/google/callback"
              element={<GoogleCallback />}
            />
            <Route
              path="/subscribe"
              element={<Subscribe mode={mode} />}
            />
            <Route
              path="/onboarding"
              element={<Onboarding mode={mode} />}
            />
            <Route
              path="/quote/:quoteId"
              element={<PublicQuotePayment />}
            />
            <Route
              path="/pricing"
              element={<Pricing mode={mode} />}
            />
            <Route
              path="/roadmap"
              element={<Roadmap mode={mode} />}
            />
            <Route
              path="/security"
              element={<Security mode={mode} />}
            />
            <Route
              path="/security/hall-of-fame"
              element={<SecurityHallOfFame mode={mode} />}
            />
            <Route
              path="/status"
              element={<Status mode={mode} />}
            />
            <Route
              path="/training"
              element={<Videos mode={mode} />}
            />
            <Route
              path="/vulnerability-disclosure-policy"
              element={<VulnerabilityDisclosurePolicy mode={mode} />}
            />
            <Route
              path="/privacy"
              element={<PrivacyPolicy mode={mode} />}
            />
            <Route
              path="/terms"
              element={<TermsOfService mode={mode} />}
            />
            <Route
              path="/blog"
              element={<Login mode={mode} isLoginParam={true} />}
            />
            <Route
              path="/blog/:name"
              element={<Login mode={mode} isLoginParam={true} />}
            />
            <Route
              path="/dashboard/:screenName"
              element={
                <Dashboard
                  setThemeMode={setThemeMode}
                  mode={mode}
                />
              }
            />
            <Route
              path="/dashboard/:screenName/:companyId"
              element={
                <Dashboard
                  setThemeMode={setThemeMode}
                  mode={mode}
                />
              }
            />
            <Route
              path="/dashboard/:screenName/:companyId/:subScreenName"
              element={
                <Dashboard
                  setThemeMode={setThemeMode}
                  mode={mode}
                />
              }
            />
            <Route
              path="/dashboard/:screenName/:companyId/:subScreenName/:instanceId"
              element={
                <Dashboard
                  setThemeMode={setThemeMode}
                  mode={mode}
                />
              }
            />
            <Route
              path="/dashboard/code/tasks/:taskId"
              element={
                <Dashboard
                  setThemeMode={setThemeMode}
                  mode={mode}
                />
              }
            />
          </Routes>
        </Router>
      </GoogleOAuthProvider>
    </ThemeProvider>
  );
}
